/**
 * Single source per il prezzo "nell'unità del rental_mode" della barca.
 *
 * Modello (2026-06): ogni barca ha il prezzo nella propria unità —
 * price_per_hour (hourly), price_per_day (daily), price_per_week (weekly),
 * price_per_month (monthly). `price_per_day` resta come FALLBACK durante la
 * transizione (×7 / ×30) finché non viene azzerato per le weekly/monthly.
 *
 * Due nozioni distinte, NON confonderle:
 *  - getUnitPrice()        → prezzo da MOSTRARE in headline (es. 3570 "a settimana").
 *  - getPerDayEquivalent() → rate per-giorno per il CALCOLO booking/listing
 *                            (es. 3570/7 = 510). Per le weekly pro_rata coincide
 *                            col vecchio price_per_day → zero regressione sui totali.
 */

export type PartialBillingMode = 'pro_rata' | 'whole_periods' | 'periods_plus_remainder';

/** Giorni per unità di periodo (hourly gestito a parte, unità = ora). */
export const RENTAL_PERIOD_DAYS: Record<string, number> = { daily: 1, weekly: 7, monthly: 30 };

export interface RentalPriceSource {
  rental_mode?: string | null;
  price_per_hour?: number | string | null;
  price_per_day?: number | string | null;
  price_per_week?: number | string | null;
  price_per_month?: number | string | null;
  partial_billing_mode?: string | null;
}

const toNum = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : null;
};
const round2 = (n: number): number => Math.round(n * 100) / 100;

const resolveMode = (src: RentalPriceSource, override?: string | null): string =>
  (override ?? src.rental_mode ?? 'daily');

/**
 * Prezzo nell'unità del rental_mode (per la headline). Fallback su price_per_day
 * (×7/×30) finché le colonne dedicate non sono popolate / price_per_day azzerato.
 */
export function getUnitPrice(src: RentalPriceSource, rentalMode?: string | null): number | null {
  const mode = resolveMode(src, rentalMode);
  const day = toNum(src.price_per_day);
  switch (mode) {
    case 'hourly': return toNum(src.price_per_hour) ?? day;
    case 'weekly': return toNum(src.price_per_week) ?? (day != null ? round2(day * 7) : null);
    case 'monthly': return toNum(src.price_per_month) ?? (day != null ? round2(day * 30) : null);
    default: return day;
  }
}

/**
 * Rate per-giorno equivalente (per calcolo booking pro-rata, payout, listings).
 * weekly → unit/7, monthly → unit/30, daily → unit, hourly → price_per_hour (unità ora).
 */
export function getPerDayEquivalent(src: RentalPriceSource, rentalMode?: string | null): number | null {
  const mode = resolveMode(src, rentalMode);
  if (mode === 'hourly') return toNum(src.price_per_hour) ?? toNum(src.price_per_day);
  const unit = getUnitPrice(src, mode);
  if (unit == null) return null;
  const days = RENTAL_PERIOD_DAYS[mode] ?? 1;
  return round2(unit / days);
}

export function resolvePartialBillingMode(src: RentalPriceSource): PartialBillingMode {
  const m = src.partial_billing_mode;
  return m === 'whole_periods' || m === 'periods_plus_remainder' ? m : 'pro_rata';
}

/**
 * Totale base (pre-extra, pre-stagioni) per un noleggio.
 * - daily/hourly o pro_rata → perDayRate × giorni (+ half).
 * - whole_periods → unitPrice × ceil(giorni/periodo).
 * - periods_plus_remainder → unitPrice × periodi interi + perDayRate × resto.
 * Per pro_rata su weekly il risultato == vecchio price_per_day × giorni.
 */
export function computeRentalBaseTotal(opts: {
  rentalMode?: string | null;
  unitPrice: number;          // prezzo nell'unità (per weekly/monthly billing intero)
  perDayRate: number;         // rate per-giorno equivalente
  fullDays: number;
  halfDays?: number;
  halfRate?: number;
  partialBillingMode?: PartialBillingMode;
}): number {
  const { rentalMode, unitPrice, perDayRate, fullDays, halfDays = 0, halfRate = 0 } = opts;
  const mode = rentalMode ?? 'daily';
  const periodDays = RENTAL_PERIOD_DAYS[mode] ?? 1;
  const proRata = perDayRate * fullDays + halfRate * halfDays;
  const billing = opts.partialBillingMode ?? 'pro_rata';
  if (mode === 'daily' || mode === 'hourly' || periodDays === 1 || billing === 'pro_rata') {
    return round2(proRata);
  }
  if (billing === 'whole_periods') {
    const periods = Math.max(1, Math.ceil((fullDays + halfDays) / periodDays));
    return round2(unitPrice * periods);
  }
  // periods_plus_remainder
  const whole = Math.floor(fullDays / periodDays);
  const rem = fullDays - whole * periodDays;
  return round2(unitPrice * whole + perDayRate * rem + halfRate * halfDays);
}

export { priceUnitLabel } from './price-unit';
